const DEFAULT_MAX_ATTEMPT_COUNT = 10;
const DEFAULT_FIRST_RETRY_DELAY_MILLISECONDS = 100;
const DEFAULT_DELAY_EXPONENT = 3;

const wait = (milliseconds) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, milliseconds);
  });
};

const computeWaitTimeMs = (attempts, firstDelayMs, delayExponent) => {
  return firstDelayMs * delayExponent ** (attempts - 1);
};

class Retrier {
  constructor(args) {
    this.args = args;
    this.attempts = 0;
  }

  makeAttempt() {
    this.attempts += 1;
    return this.args.asyncAction().then((result) => {
      return result;
    }).catch((reason) => {
      if (this.attempts >= this.args.maxAttemptCount) {
        throw reason;
      }
      return wait(computeWaitTimeMs(
        this.attempts,
        this.args.firstRetryDelayMs,
        this.args.delayExponent
      )).then(() => {
        return this.makeAttempt();
      });
    });
  }
}

/**
 * Retries an asynchronous operation a certain number of times, using an exponential back-off
 * strategy to wait longer and longer between each failed attempt before trying again.
 * @param {Function} asyncAction - Operation to attempt, must return a Promise.
 * @param {number} maxAttemptCount - Maximum number of total attempts.
 * @param {number} firstRetryDelayMs - Time to wait before trying again for the first time, in
 * milliseconds.
 * @param {number} delayExponent - Exponent to calculate subsequent delays between attempts.
 * @returns {Promise} - Either: (1) resolves to the value that the Promise returned by asyncAction
 * resolved to; or (2) rejects with the value that the Promise returned by asyncAction most recently
 * rejected with.
 */
const retry = ({
  asyncAction,
  maxAttemptCount = DEFAULT_MAX_ATTEMPT_COUNT,
  firstRetryDelayMs = DEFAULT_FIRST_RETRY_DELAY_MILLISECONDS,
  delayExponent = DEFAULT_DELAY_EXPONENT,
}) => {
  return new Retrier({
    asyncAction,
    maxAttemptCount,
    firstRetryDelayMs,
    delayExponent,
  }).makeAttempt();
};

export default retry;

export const unitTestExports = {
  DEFAULT_FIRST_RETRY_DELAY_MILLISECONDS,
  DEFAULT_MAX_ATTEMPT_COUNT,
  computeWaitTimeMs,
};
